/* eslint-disable no-restricted-syntax */
/* eslint-disable no-underscore-dangle */
import { put, select, takeLatest } from 'redux-saga/effects';

import { applicationStateSelectors } from 'store/slices/applicationState/selectors';
import { loadingRequired, loadingStopRequired, setConfigurations } from 'store/slices/configurations/slice';
import { getClientIdSelector } from 'store/slices/auth/selectors';
import { environmentSelectors } from 'store/slices/environment/selector';
import { EnvironmentState } from 'store/slices/environment/slice';

import mockImage1 from 'assets/images/mock/16_9 _ 1.png';
import mockImage2 from 'assets/images/mock/16_9 _ 2.png';
import mockImage3 from 'assets/images/mock/16_9 _ 3.png';
import mockImage4 from 'assets/images/mock/16_9 _ 4.png';

import type { CardApartmentProps } from 'components/CardApartment/CardApartment';
import type { ApartmentConfiguration, Building, RoomConfiguration } from 'types/configurations';
import type { ExtraSpace } from 'types/expenses';
import type { Quote, QuoteStatus, Statuses, UserRequest } from 'types/quote';

import { configurationsActions } from './configurations.actions';
import { getRequestByClientId, getSpace, getSpaces } from './configurations.api';
import { mapApartmentRequestToQuote, mapRoomRequestToQuote, mapUserRawRequest } from 'utils/userRequestMappers';
import { SpaceType, AssetType } from 'types/project';
import { getImagePaths } from 'utils/getImagePaths';
import { getRoomStatus, sortQuotesByStatus } from 'utils/status';
import { INVALID_STATUSES, PRIORITY_STATUSES, VALID_STATUSES } from 'constants/quoteStatus';

const IMAGES = [mockImage1, mockImage2, mockImage3, mockImage4];
const FEATURES: ExtraSpace[] = [
  {
    id: '123',
    name: 'CANTINA 1',
    type: 'CANTINA',
    price: 300,
    floor: 1,
    sub: 799,
  },
  {
    id: '234',
    name: 'CANTINA 2',
    type: 'CANTINA',
    price: 300,
    floor: 0,
    sub: 799,
  },
  {
    id: '345',
    name: 'BOX 1',
    type: 'BOX',
    price: 300,
    floor: 1,
    sub: 799,
  },
];
const PACKS = [
  {
    name: 'Outdoor Pack',
    price: 210,
  },
  {
    name: 'Curtain Pack',
    price: 300,
  },
  {
    name: 'Extra Decor',
    price: 50,
  },
];
// const QUOTES: Quote[] = [
//   {
//     id: '1',
//     name: 'Quote #1',
//     status: 'confirmed',
//     price: 1648,
//     dateRange: {
//       checkInDate: '15 Jan 2024',
//       checkOutDate: '15 Jan 2028',
//     },
//     features: FEATURES,
//     packs: PACKS,
//     mood: 'Bright',
//     createdOn: '2024-08-04',
//   },
//   {
//     id: '2',
//     name: 'Quote #2',
//     price: 2737,
//     dateRange: {
//       checkInDate: '25 Jan 2024',
//       checkOutDate: '25 Jan 2028',
//     },
//     features: FEATURES,
//     packs: PACKS,
//     createdOn: '2024-02-21',
//   },
//   {
//     id: '3',
//     name: 'Quote #3',
//     status: 'expired',
//     price: 3888,
//     dateRange: {
//       checkInDate: '7 Feb 2024',
//       checkOutDate: '7 Feb 2028',
//     },
//     features: FEATURES,
//     packs: PACKS,
//     createdOn: '2024-03-13',
//   },
//   {
//     id: '4',
//     name: 'Quote #4',
//     price: 1111,
//     status: 'archived',
//     dateRange: {
//       checkInDate: '17 Feb 2024',
//       checkOutDate: '17 Feb 2028',
//     },
//     features: FEATURES,
//     createdOn: '2024-01-01',
//   },
//   {
//     id: '5',
//     name: 'Quote #5',
//     status: 'expired',
//     price: 2223,
//     dateRange: {
//       checkInDate: '11 Feb 2024',
//       checkOutDate: '11 Feb 2028',
//     },
//     features: FEATURES,
//     createdOn: '2024-01-01',
//   },
// ];
// const QUOTES_WITHOUT_CONFIRMED = QUOTES.filter((quote) => quote.status !== 'confirmed');
// const QUOTES_EXPIRED = QUOTES.filter((quote) => quote.status === 'expired');

const DEFAULT_BUILDING: Building = {
  id: '',
  zone: '',
  address: '',
  name: '',
  complex: '',
  code: '',
};

const getApartmentStatus = (data: ApartmentConfiguration, quotes?: Quote[]) => {
  if (!data.available) {
    return 'unavailable';
  }
  if (quotes?.length) {
    if (quotes.some((quote) => VALID_STATUSES.includes(quote.status))) {
      for (const status of PRIORITY_STATUSES) {
        const found = quotes.find((quote) => quote.status === status);
        if (found) return status;
      }
    }
    if (quotes.some((quote) => quote.status === 'suspended')) {
      return 'suspended';
    }
    if (quotes.every((quote) => INVALID_STATUSES.includes(quote.status))) {
      return 'invalid';
    }
  }
  return 'interest';
};

export const sanitizeApartmentConfig = (data: ApartmentConfiguration, quotes?: Quote[]): CardApartmentProps => {
  if (data) {
    return {
      building: data.building,
      name: data.name,
      typologyName: data.plan.typology.name,
      id: data._id,
      surfaceArea: data.plan.surfaceArea,
      floor: data.floor,
      features: {
        ...data.plan.mainFeatures,
        floor: data.floor,
        buildingName: data.building?.name,
        side: data.sides?.length ? data.sides[0].name : undefined,
        surface: data?.plan?.surfaceArea?.total,
      },
      images: IMAGES,
      quotes: quotes || [],
      planName: data.plan.name,
      status: getApartmentStatus(data, quotes) as QuoteStatus,
      code: data.code,
    };
  }
  return {
    building: DEFAULT_BUILDING,
    name: '',
    typologyName: '',
    id: '',
    surfaceArea: {
      apartment: 0,
      garden: 0,
      penthouse: 0,
      commercial: 0,
      balcony: 0,
      loggia: 0,
      terrace: 0,
      total: 0,
      attic: 0,
    },
    floor: 0,
    features: {},
    images: [],
    quotes: [],
    planName: '',
    status: 'init',
  };
};

const sanitizeRoomConfig = ({
  room,
  buildings,
  images,
  quotes,
  apartment,
}: {
  room: RoomConfiguration;
  buildings: Building[];
  images: string[];
  quotes?: Quote[];
  apartment?: CardApartmentProps;
}): CardApartmentProps => {
  const building = buildings.find((building) => building.id === room.building) ?? DEFAULT_BUILDING;
  const roomQuotes = quotes?.filter((quote) => quote.spaceId === room.id);

  return {
    building,
    name: room.name,
    typologyName: room.model,
    id: room.id,
    floor: room.floor,
    features: {
      floor: room.floor,
      surface: room.surface,
      privateBathroom: room.bathroom,
      buildingName: building.name,
    },
    images: images,
    quotes: roomQuotes || [],
    planName: apartment?.planName,
    planNameRoom: room.plan,
    status: getRoomStatus(roomQuotes || []),
    code: apartment?.code,
    apartmentData: apartment,
  };
};

/**
 * Fetches raw user requests from the request API and normalizes them into the
 * shared request model used by both room and apartment dashboard flows.
 */
export const getUserRequests = async (
  projectId: string,
  clientId: string,
  statuses: Statuses,
  spaceId?: string,
): Promise<UserRequest[]> => {
  const response = await getRequestByClientId(projectId, clientId, statuses, spaceId);
  if (response.ok) {
    const data = (await response.json()) as Parameters<typeof mapUserRawRequest>[0][];
    return data.map((rawUserRequest) => mapUserRawRequest(rawUserRequest, statuses));
  }
  return [];
};

const getBuildingsBySpaceIds = async (projectId: string, spaceIds: string[]): Promise<Building[]> => {
  const response = await getSpaces(projectId, spaceIds, 'building');
  if (response.ok) {
    const { data } = await response.json();
    return data;
  }
  return [];
};

// FIXME: Remove mocks once got Quote endpoint
const addMockData = (id: string) => {
  // if (id === '61c4713562720c64f06dcbe1') {
  //   return QUOTES_WITHOUT_CONFIRMED;
  // }
  // if (id === '61c4713562720c64f06dcbdc') {
  //   return QUOTES_EXPIRED;
  // }
  // return QUOTES;
  return [];
};

/**
 * Resolves room slider images using the legacy room bucket convention.
 * This branch is kept stable to avoid regressions on the existing room flow.
 */
const getRoomImages = async (data: RoomConfiguration[], buildings: Building[], imagesBaseUrl: string) => {
  const images = await Promise.all(
    data.map(async (room: RoomConfiguration) => {
      const building = buildings.find((currentBuilding) => currentBuilding.id === room.building);
      const sliderImagePath = `${imagesBaseUrl}/global/img/slider/${building?.complex}/room/${room.model}/default/`;
      const paths = await getImagePaths({
        basePath: sliderImagePath,
        pathExtension: '.jpg',
      });
      return paths;
    }),
  );
  return images;
};

/**
 * Resolves apartment slider images using the apartment bucket convention
 * based on complex, apartment type and the apartment mood returned by the
 * asset API. It tries `.jpg` first, then `.jpeg`, and finally falls back to
 * mock images when no bucket assets are available.
 */
const getApartmentImages = async (data: ApartmentConfiguration[], imagesBaseUrl: string) => {
  const images = await Promise.all(
    data.map(async (apartment: ApartmentConfiguration) => {
      if (!apartment.building?.complex || !apartment.plan?.typology?.name || !apartment.mood) {
        return IMAGES;
      }

      const basePath = `${imagesBaseUrl}/global/img/slider/${encodeURI(apartment.building.complex)}/apartment/${encodeURI(
        apartment.plan.typology.name,
      )}/${encodeURI(apartment.mood)}/`;

      const jpgPaths = await getImagePaths({
        basePath,
        pathExtension: '.jpg',
      });
      if (jpgPaths.length) return jpgPaths;

      const jpegPaths = await getImagePaths({
        basePath,
        pathExtension: '.jpeg',
      });
      return jpegPaths.length ? jpegPaths : IMAGES;
    }),
  );
  return images;
};

const getLatestQuoteTimestamp = (quotes?: Quote[]) => {
  if (!quotes?.length) return 0;
  return quotes.reduce((latest, quote) => {
    const timestamp = new Date(quote.createdOn).getTime();
    return Number.isNaN(timestamp) ? latest : Math.max(latest, timestamp);
  }, 0);
};

const getPriorityStatusIndex = (status: QuoteStatus) => {
  const index = PRIORITY_STATUSES.indexOf(status);
  return index === -1 ? PRIORITY_STATUSES.length : index;
};

const sortByLatestQuoteThenStatus = (a: CardApartmentProps, b: CardApartmentProps) => {
  const dateDelta = getLatestQuoteTimestamp(b.quotes) - getLatestQuoteTimestamp(a.quotes);
  if (dateDelta !== 0) return dateDelta;
  return getPriorityStatusIndex(a.status as QuoteStatus) - getPriorityStatusIndex(b.status as QuoteStatus);
};

/**
 * Builds dashboard card data for room assets.
 * This keeps the pre-existing room pipeline intact and only enriches it with
 * the normalized request-derived quotes provided by the shared fetch step.
 */
const getRoomConfigurations = async (projectId: string, spaceIds: string[], quotes?: Quote[], imagesBaseUrl?: string) => {
  try {
    const response = await getSpaces(projectId, spaceIds, 'room');
    if (response.ok) {
      const { data } = await response.json();
      const buildings = await getBuildingsBySpaceIds(
        projectId,
        data.map((room: RoomConfiguration) => room.building),
      );
      const images = await getRoomImages(data, buildings, imagesBaseUrl ?? '');
      const sanitizedRooms = data.map((room: RoomConfiguration, index: number) =>
        sanitizeRoomConfig({ room, buildings, quotes, images: images[index] }),
      );
      return sanitizedRooms.slice().sort(sortByLatestQuoteThenStatus);
    }
    throw new Error('Response not OK');
  } catch (error) {
    console.log(error);
    return undefined;
  }
};

/**
 * Builds dashboard card data for apartment assets.
 * Compared to the room flow, this branch derives slider images from apartment
 * quotes so the selected mood can be reflected in the bucket path.
 */
const getApartmentConfigurations = async (projectId: string, spaceIds: string[], quotes?: Quote[], imagesBaseUrl?: string) => {
  try {
    const response = await getSpaces(projectId, spaceIds, 'apartment');
    if (response.ok) {
      const { data } = await response.json();
      const images = await getApartmentImages(data, imagesBaseUrl ?? '');
      const apartments: CardApartmentProps[] = data.map((space: ApartmentConfiguration, index: number) => {
        const spaceQuotes = quotes?.filter((quote) => quote.spaceId === space._id) ?? addMockData(space._id);
        return {
          ...sanitizeApartmentConfig(space, spaceQuotes),
          images: images[index],
        };
      });
      return apartments.slice().sort(sortByLatestQuoteThenStatus);
    }
    throw new Error('Response not OK');
  } catch (error) {
    console.log(error);
    return undefined;
  }
};

/**
 * Resolves the room detail card payload, including the related apartment data
 * needed by the existing room detail view.
 */
const getRoomConfiguration = async (projectId: string, spaceId: string, quotes?: Quote[], imagesBaseUrl?: string) => {
  try {
    const res = await getSpace(projectId, spaceId, 'room');
    if (res.ok) {
      const { data } = await res.json();
      const buildings = await getBuildingsBySpaceIds(projectId, [data.building]);
      const apartmentDataResponse = await getSpace(projectId, data.apartment, 'apartment');
      const { data: apartmentData } = await apartmentDataResponse.json();

      const images = await getRoomImages([data], buildings, imagesBaseUrl ?? '');
      return sanitizeRoomConfig({ room: data, buildings, quotes, images: images[0], apartment: sanitizeApartmentConfig(apartmentData) });
    }
    throw new Error('Response not OK');
  } catch (error) {
    console.log(error);
    return undefined;
  }
};

/**
 * Resolves the apartment detail card payload, including apartment slider
 * images derived from the selected mood when available.
 */
const getApartmentConfiguration = async (projectId: string, spaceId: string, quotes?: Quote[], imagesBaseUrl?: string) => {
  try {
    const res = await getSpace(projectId, spaceId, 'apartment');
    if (res.ok) {
      const { data } = await res.json();
      /**  FIXME: temp mock for UI check */
      if (spaceId === '61aa1d1ade0523aff25e67b6') {
        return {
          ...sanitizeApartmentConfig(data),
          status: 'proposal' as QuoteStatus,
        };
      }
      const images = await getApartmentImages([data], imagesBaseUrl ?? '');
      const spaceQuotes = quotes?.filter((quote) => quote.spaceId === data._id) ?? addMockData(data._id);
      return {
        ...sanitizeApartmentConfig(data, spaceQuotes),
        images: images[0],
      };
    }
    throw new Error('Response not OK');
  } catch (error) {
    console.log(error);
    return undefined;
  }
};

/**
 * Dispatches the asset fetch to the room or apartment configuration pipeline
 * after requests have already been normalized and branched by asset type.
 */
const getConfigurationsByAssetType = async (
  projectId: string,
  spaceIds: string[],
  assetType: AssetType,
  quotes?: Quote[],
  imagesBaseUrl?: string,
) => {
  if (assetType === 'room') {
    return getRoomConfigurations(projectId, spaceIds, quotes, imagesBaseUrl);
  }
  return getApartmentConfigurations(projectId, spaceIds, quotes, imagesBaseUrl);
};

/**
 * Shared detail entrypoint used by the details page. The underlying resolver
 * is selected by asset type so room and apartment detail flows can diverge
 * without changing the page API.
 */
export const getApartment = async (projectId: string, spaceId: string, spaceType: SpaceType, quotes?: Quote[], imagesBaseUrl?: string) => {
  if (spaceType === 'room') {
    return getRoomConfiguration(projectId, spaceId, quotes, imagesBaseUrl);
  }
  return getApartmentConfiguration(projectId, spaceId, quotes, imagesBaseUrl);
};

/**
 * Converts normalized user requests into the quote model consumed by the UI.
 * This is the first explicit room/apartment branching point after the shared
 * request fetch step.
 */
const mapRequestsByAssetType = (userRequests: UserRequest[], assetType: AssetType) => {
  if (assetType === 'room') {
    return userRequests.map((userRequest) => mapRoomRequestToQuote(userRequest));
  }
  return userRequests.map((userRequest) => mapApartmentRequestToQuote(userRequest));
};

/**
 * Dashboard entrypoint: fetches user requests, branches the request-to-quote
 * mapping by asset type, then loads and stores the corresponding card data.
 */
function* initConfigurations() {
  yield put(loadingRequired());
  const projectId: string = yield select(applicationStateSelectors.selectProjectId);
  const projectDisplayName: string = yield select(applicationStateSelectors.selectDisplayName);
  const assetTypology: AssetType = yield select(applicationStateSelectors.selectAssetTypology);
  const environment: EnvironmentState = yield select(environmentSelectors.environment);
  const clientId: string = yield select(getClientIdSelector);
  const statuses: Statuses = yield select(applicationStateSelectors.selectStatuses);
  const userRequests: UserRequest[] = yield getUserRequests(projectId, clientId, statuses);
  // Avoid sending null/anonymous spaceIds (e.g. "Feature" requests without a space) that would 400 the asset API.
  const filteredUserRequests = userRequests.filter(
    (userRequest) => userRequest.spaceId && userRequest.spaceType?.toLowerCase() === assetTypology,
  );

  if (filteredUserRequests.length) {
    const spaceIds = [...new Set(filteredUserRequests.map((userRequest) => userRequest.spaceId))];
    const quotes = mapRequestsByAssetType(filteredUserRequests, assetTypology);
    const sortedQuotes = sortQuotesByStatus(quotes);
    const imagesBaseUrl = `${environment.REACT_APP_BUCKET_BASEURL}/initiatives/${encodeURI(projectDisplayName)}`;
    const configurations: CardApartmentProps[] = yield getConfigurationsByAssetType(
      projectId,
      spaceIds,
      assetTypology,
      sortedQuotes,
      imagesBaseUrl,
    );
    yield put(setConfigurations(configurations));
  }
  yield put(loadingStopRequired());
}

export function* configurationsSaga() {
  yield takeLatest(configurationsActions.initConfigurations, initConfigurations);
}
